// Financial Reporting tab — the issued requests of the selected type.
//
// One row per request, newest first, and the WHOLE ROW opens it. It used to be
// a card whose left-hand title block was the only clickable thing, expanding an
// item list in place while three other buttons on the same card each opened a
// different surface; the row now has one destination — the request's lifecycle
// drawer — and every action lives inside it, in the stage it belongs to.
//
// The two confirmations stay HERE rather than inside the drawer. Both are
// modals, both are opened from the drawer, and a modal mounted inside a
// slide-over that the modal's own success then closes is a teardown race with
// nothing to gain: the list outlives both.
//
// Complete-with-exceptions keeps its full-sentence dialog because its
// consequence needs saying: it unblocks report generation while items are still
// missing, and the generated report names what was never provided. That is a
// deliberate trade, not a tidy-up, so the dialog states it and requires a note.

import { useState } from "react";
import { ChevronRight } from "lucide-react";
import {
  Button,
  EmptyState,
  StatusBadge,
  type StatusTone,
  Textarea,
  useToast,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";
import { formatCalendarDate, formatDateInTz } from "@/design-system/lib/format";
import {
  REPORT_REQUEST_STATUS_LABEL,
  type ReportRequestDto,
} from "@/api/portfolioAdmin/reportRequestsApi";
import {
  useCancelReportRequest,
  useCompleteRequestWithExceptions,
} from "@/hooks/portfolioAdmin/useReportRequests";
import { isReportRequestOverdue } from "../defaults/timeline";
import { ReportRequestLifecycleDrawer } from "./ReportRequestLifecycleDrawer";
import { awaitingReviewRows, byNewestIssued } from "./reportRequestSummary";

interface ReportRequestListProps {
  loanId: string;
  /** Already narrowed to the selected type by the section. */
  requests: ReportRequestDto[];
  /** Reporting.ReviewRequests — gates every write on this surface. */
  canReview: boolean;
  /** Opened by the attention strip / a header's "open it" — cleared on close. */
  openRequestId: string | null;
  onOpenRequestChange: (requestId: string | null) => void;
  /** What the empty state should say for the selected type. */
  emptyTitle: string;
  emptyDescription: string;
  tz?: string | null;
}

function requestTone(status: string): StatusTone {
  switch (status) {
    case "complete":
    case "reported":
      return "ok";
    case "under-review":
      return "progress";
    case "in-progress":
      return "info";
    case "cancelled":
      return "muted";
    default:
      return "neutral";
  }
}

export function ReportRequestList({
  loanId,
  requests,
  canReview,
  openRequestId,
  onOpenRequestChange,
  emptyTitle,
  emptyDescription,
  tz,
}: ReportRequestListProps) {
  const toast = useToast();
  const complete = useCompleteRequestWithExceptions(loanId);
  const cancel = useCancelReportRequest(loanId);

  const [exceptionsTarget, setExceptionsTarget] = useState<ReportRequestDto | null>(null);
  const [exceptionsNote, setExceptionsNote] = useState("");
  const [cancelTarget, setCancelTarget] = useState<ReportRequestDto | null>(null);
  const [cancelNote, setCancelNote] = useState("");

  // The drawer stays OPEN across its own mutations (approve an item, generate,
  // then file), so it holds an id and the live row is re-derived every render.
  // A captured DTO would keep showing "Generate report" after generating one —
  // the refetched row would never reach it.
  const open = requests.find((r) => r.id === openRequestId) ?? null;

  const submitExceptions = () => {
    if (!exceptionsTarget) return;
    complete.mutate(
      { requestId: exceptionsTarget.id, note: exceptionsNote.trim() },
      {
        onSuccess: () => {
          setExceptionsTarget(null);
          setExceptionsNote("");
          toast.success("Marked complete with exceptions.");
        },
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  };

  const submitCancel = () => {
    if (!cancelTarget) return;
    cancel.mutate(
      { requestId: cancelTarget.id, note: cancelNote.trim() },
      {
        onSuccess: () => {
          setCancelTarget(null);
          setCancelNote("");
          onOpenRequestChange(null);
          toast.success("Request cancelled.");
        },
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  };

  return (
    <>
      {requests.length === 0 ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        <ul className="flex flex-col gap-space-2 list-none p-0 m-0">
          {byNewestIssued(requests).map((request) => {
            const overdue = isReportRequestOverdue(request);
            const awaiting = awaitingReviewRows([request]).length;

            return (
              <li key={request.id}>
                {/* Whole-row control. A row whose only door is its title block
                    means every miss lands on dead space inside something that
                    looks entirely clickable. */}
                <button
                  type="button"
                  onClick={() => onOpenRequestChange(request.id)}
                  className="w-full flex flex-wrap items-center justify-between gap-space-3 rounded-lg border border-border-light bg-white-pure px-space-4 py-space-3 text-left hover:border-gold focus-visible:outline-none focus-visible:shadow-goldRing"
                  data-testid={`report-request-${request.id}`}
                >
                  <span className="min-w-0 flex items-center gap-space-2">
                    <ChevronRight size={16} aria-hidden className="shrink-0 text-gray-mid" />
                    <span className="min-w-0">
                      <span className="block text-small font-semibold text-onyx">
                        {request.periodLabel}
                      </span>
                      <span className="block text-caption text-gray-mid">
                        Due{" "}
                        <span
                          className={overdue ? "font-semibold text-status-error" : undefined}
                        >
                          {formatCalendarDate(request.dueDate)}
                        </span>
                        {overdue ? " · Overdue" : ""} · issued{" "}
                        {formatDateInTz(request.issuedAt, tz)}
                        {request.issuedByUserName ? ` by ${request.issuedByUserName}` : ""}
                      </span>
                    </span>
                  </span>

                  <span className="flex flex-wrap items-center gap-space-2 shrink-0">
                    {/* Derived from `awaitingReview`, the only field that
                        carries "the borrower has sent this and nobody has
                        looked": nothing writes the `submitted` item status. */}
                    {awaiting > 0 && (
                      <StatusBadge
                        tone="info"
                        data-testid={`report-request-awaiting-${request.id}`}
                      >
                        {awaiting} waiting for review
                      </StatusBadge>
                    )}
                    {request.canGenerateReport && (
                      <StatusBadge
                        tone="progress"
                        data-testid={`report-request-ready-${request.id}`}
                      >
                        Ready to report
                      </StatusBadge>
                    )}
                    <StatusBadge tone={requestTone(request.status)}>
                      {REPORT_REQUEST_STATUS_LABEL[request.status] ?? request.status}
                    </StatusBadge>
                    {request.completeWithExceptions && (
                      <StatusBadge tone="warn">With exceptions</StatusBadge>
                    )}
                    <span
                      className="text-caption text-gray-mid"
                      data-testid={`report-request-counts-${request.id}`}
                    >
                      {request.approvedCount}/{request.itemCount} approved
                      {request.outstandingCount > 0
                        ? ` · ${request.outstandingCount} outstanding`
                        : ""}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <ReportRequestLifecycleDrawer
        loanId={loanId}
        request={open}
        canReview={canReview}
        tz={tz}
        onClose={() => onOpenRequestChange(null)}
        onCancelRequest={(request) => {
          setCancelNote("");
          setCancelTarget(request);
        }}
        onCompleteWithExceptions={(request) => {
          setExceptionsNote("");
          setExceptionsTarget(request);
        }}
      />

      {/* Complete with exceptions — the consequence is the whole point of the
          dialog, so it is stated rather than implied. */}
      <Modal
        open={exceptionsTarget !== null}
        onClose={() => setExceptionsTarget(null)}
        title="Complete with exceptions"
        description={
          exceptionsTarget
            ? `${exceptionsTarget.requestTypeLabel} · ${exceptionsTarget.periodLabel}`
            : undefined
        }
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setExceptionsTarget(null)}
              disabled={complete.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={complete.isPending}
              disabled={exceptionsNote.trim().length === 0}
              onClick={submitExceptions}
              data-testid="exceptions-confirm"
            >
              Complete with exceptions
            </Button>
          </>
        }
      >
        <p className="text-small text-onyx m-0">
          {exceptionsTarget?.outstandingCount ?? 0} item
          {(exceptionsTarget?.outstandingCount ?? 0) === 1 ? " is" : "s are"} still
          outstanding. Completing with exceptions lets the report be generated
          anyway — and the generated report will <strong>name what is still
          missing</strong>. The borrower&apos;s outstanding items are not withdrawn.
        </p>
        <label
          className="block text-small font-medium text-onyx mt-space-3 mb-space-1"
          htmlFor="exceptions-note"
        >
          Why you are completing without them <span className="text-status-error">*</span>
        </label>
        <Textarea
          id="exceptions-note"
          value={exceptionsNote}
          onChange={(e) => setExceptionsNote(e.target.value)}
          rows={3}
          placeholder="This note is part of the record and explains the gap…"
          data-testid="exceptions-note"
        />
      </Modal>

      <Modal
        open={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        title="Cancel request"
        description={
          cancelTarget
            ? `${cancelTarget.requestTypeLabel} · ${cancelTarget.periodLabel}`
            : undefined
        }
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setCancelTarget(null)}
              disabled={cancel.isPending}
            >
              Keep it
            </Button>
            <Button
              variant="destructive"
              loading={cancel.isPending}
              disabled={cancelNote.trim().length === 0}
              onClick={submitCancel}
              data-testid="cancel-confirm"
            >
              Cancel request
            </Button>
          </>
        }
      >
        <p className="text-small text-onyx m-0">
          The borrower stops being asked for anything on this request.
        </p>
        <label
          className="block text-small font-medium text-onyx mt-space-3 mb-space-1"
          htmlFor="cancel-note"
        >
          Reason <span className="text-status-error">*</span>
        </label>
        <Textarea
          id="cancel-note"
          value={cancelNote}
          onChange={(e) => setCancelNote(e.target.value)}
          rows={3}
          placeholder="Why this request is no longer needed…"
          data-testid="cancel-note"
        />
      </Modal>
    </>
  );
}
